import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { BRAND_GREEN_PRIMARY, DARKER_TEXT, DARK_TEXT } from 'resources/constants/colors';

// ---------------------------------------------------------------------------
// Daily insight pool — seeded by day of year for consistency.
// Replace `getDailyInsight()` with an API call to integrate real AI.
// ---------------------------------------------------------------------------

const INSIGHTS = [
    {
        icon: '🌬️',
        category: 'Respiración',
        title: 'Respira para calmarte',
        body: 'Inhala 4 segundos, retén 4, exhala 4. Repite 3 veces. Tu sistema nervioso lo agradecerá.',
        color: '#e3f2fd',
        accent: '#1976d2',
    },
    {
        icon: '🌿',
        category: 'Mindfulness',
        title: 'Un minuto presente',
        body: 'Nombra 5 cosas que puedes ver ahora mismo. Este ejercicio ancla tu mente al momento presente.',
        color: '#f1f8e9',
        accent: '#7cb342',
    },
    {
        icon: '💧',
        category: 'Autocuidado',
        title: 'Hidratación y bienestar',
        body: 'Beber agua suficiente mejora el estado de ánimo y la concentración. ¿Ya bebiste un vaso hoy?',
        color: '#e8f4f8',
        accent: '#0288d1',
    },
    {
        icon: '🚶',
        category: 'Movimiento',
        title: 'Muévete 10 minutos',
        body: 'Una caminata corta libera endorfinas y reduce el estrés. No necesitas equipo ni tiempo extra.',
        color: '#fff3e0',
        accent: '#ef8c35',
    },
    {
        icon: '📝',
        category: 'Diario emocional',
        title: 'Escribe lo que sientes',
        body: 'Tres líneas al día sobre tus emociones pueden ayudarte a detectar patrones y sanar más rápido.',
        color: '#fce4ec',
        accent: '#e91e63',
    },
    {
        icon: '🌙',
        category: 'Sueño',
        title: 'El descanso es terapia',
        body: 'Dormir bien consolida lo aprendido en terapia. Intenta mantener un horario de sueño constante.',
        color: '#ede7f6',
        accent: '#7e57c2',
    },
    {
        icon: '🤝',
        category: 'Conexión',
        title: 'Conecta con alguien',
        body: 'Un mensaje a alguien de confianza puede romper el aislamiento. La conexión humana es medicina.',
        color: '#e8f5e9',
        accent: '#43a047',
    },
];

function getDailyInsight() {
    const dayOfYear = Math.floor(
        (Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86_400_000
    );
    return INSIGHTS[dayOfYear % INSIGHTS.length];
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
    background: ${({ bg }) => bg};
    border-radius: 20px;
    padding: 18px 20px;
    display: flex;
    gap: 14px;
    align-items: flex-start;
    animation: ${fadeUp} 0.5s 0.1s ease both;
`;

const IconBox = styled.div`
    font-size: 26px;
    line-height: 1;
    flex-shrink: 0;
    margin-top: 2px;
`;

const Content = styled.div`
    flex: 1;
    min-width: 0;
`;

const Category = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${({ accent }) => accent};
    display: block;
    margin-bottom: 3px;
`;

const Title = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 4px;
    line-height: 1.3;
`;

const Body = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 0;
    line-height: 1.55;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * WellnessInsight — shows a daily mental health tip.
 * Seeded by the current day for consistency; ready for AI API integration.
 */
const WellnessInsight = () => {
    const insight = useMemo(getDailyInsight, []);

    return (
        <Card bg={insight.color}>
            <IconBox>{insight.icon}</IconBox>
            <Content>
                <Category accent={insight.accent}>{insight.category} del día</Category>
                <Title>{insight.title}</Title>
                <Body>{insight.body}</Body>
            </Content>
        </Card>
    );
};

export default WellnessInsight;
